import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputBase,
  IconButton,
  Grid,
  Button,
  Modal,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";

import { useSelector } from "react-redux";
import createInstance from "../../../api/axiosinstance";
import Loader from "../../Loader";
import Toast from "../../../utils/toast";
import { useThemeMode } from "../../Themes/ThemeContextTemp.js";

const ZoomMeeting = () => {
  const { user } = useSelector((state) => state.auth);
  const organizationId = user?.organizationId;

  const [videos, setVideos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isLoading, setIsLoading] = useState(false);

  const [openPlayer, setOpenPlayer] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const { COLORS } = useThemeMode();

  const api = createInstance("DISHA");

  const fetchVideos = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(
        `/zoom/recorded_meeting_videos/?organization_id=${organizationId}`
      );
      setVideos(
        Array.isArray(response.data) ? [...response.data].reverse() : []
      );
    } catch (err) {
      console.error("Error fetching zoom videos:", err);
      Toast.error("Failed to fetch Zoom recordings");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) fetchVideos();
  }, [organizationId]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const filteredVideos = videos.filter((row) =>
    row.meeting_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date)) return "Invalid Date";

      return date.toLocaleString("en-IN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid Date";
    }
  };

  // const handleViewVideo = (url) => {
  //   setCurrentVideoUrl(url);
  //   setOpenPlayer(true);
  // };

  const handleViewVideo = async (meetingId) => {
  try {
    setIsLoading(true);

    const response = await api.get(
      `/api/recordings_signed_url/${meetingId}`
    );

    const recordings = response.data?.recordings;

    if (!recordings || recordings.length === 0) {
      Toast.error("No recordings found");
      return;
    }

    // Take first recording (you can enhance later)
    const videoUrl = recordings[0].presigned_url;

    setCurrentVideoUrl(videoUrl);
    setOpenPlayer(true);

  } catch (error) {
    console.error("Error fetching signed URL:", error);
    Toast.error(
      error?.response?.data?.error || "Failed to load video"
    );
  } finally {
    setIsLoading(false);
  }
};

  const header = [
    {
      meetingId: "Meeting ID",
      type: "Recording Type",
      fileSize: "File Size (MB)",
      start: "Recording Start",
      end: "Recording End",
      action: "Action",
    },
  ];

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#33333",
        }}
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#33333" }}>
      <Typography
        sx={{
          fontFamily: "Poppins",
          fontSize: "28px",
          color: COLORS.text,
          mb: 2,
        }}
      >
        Zoom Recorded Meetings
      </Typography>

      {/* Search Bar */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={{ alignItems: "center", padding: "1% 1%" }}
      >
        <Box gridColumn="span 6" sx={{ marginLeft: "8%" }}>
          <Paper
            sx={{
              p: "2px 4px",
              display: "flex",
              width: "75%",
              borderRadius: "4px",
              background: COLORS.primary,
              border: COLORS.border,
            }}
          >
            <IconButton sx={{ p: "8px", color: COLORS.text }}>
              <SearchIcon />
            </IconButton>
            <InputBase
              sx={{
                ml: 1,
                flex: 1,
                fontFamily: "Poppins",
                color: COLORS.text,
                "& input::placeholder": { 
                  color: `${COLORS.text} !important` },
              }}
              placeholder="Search by Meeting ID"
              value={searchTerm}
              onChange={handleSearch}
            />
          </Paper>
        </Box>
      </Box>

      {/* Table */}
      <Grid container spacing={1} sx={{ justifyContent: "center" }}>
        <Grid item xs={10}>
          <TableContainer
            component={Paper}
            sx={{ marginTop: "25px", borderRadius: "10px" }}
          >
            <Table size="small">
              <TableHead sx={{ background: COLORS.primary }}>
                {header.map((head) => (
                  <TableRow key={head.meetingId}>
                    <TableCell sx={{color: COLORS.text,
    borderBottom: COLORS.secondary,
    fontWeight: "bold",
    fontFamily: "Poppins",}}>
                      {head.meetingId}
                    </TableCell>
                    <TableCell sx={{color: COLORS.text,
    borderBottom: COLORS.secondary,
    fontWeight: "bold",
    fontFamily: "Poppins",}}>{head.type}</TableCell>
                    <TableCell sx={{color: COLORS.text,
    borderBottom: COLORS.secondary,
    fontWeight: "bold",
    fontFamily: "Poppins",}}>
                      {head.fileSize}
                    </TableCell>
                    <TableCell sx={{color: COLORS.text,
    borderBottom: COLORS.secondary,
    fontWeight: "bold",
    fontFamily: "Poppins",}}>{head.start}</TableCell>
                    <TableCell sx={{color: COLORS.text,
    borderBottom: COLORS.secondary,
    fontWeight: "bold",
    fontFamily: "Poppins",}}>{head.end}</TableCell>
                    <TableCell sx={{color: COLORS.text,
    borderBottom: COLORS.secondary,
    fontWeight: "bold",
    fontFamily: "Poppins",}}>{head.action}</TableCell>
                  </TableRow>
                ))}
              </TableHead>

              <TableBody>
                {filteredVideos
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow
                      key={item._id}
                      sx={{ background: COLORS.background }}
                    >
                      <TableCell sx={{color: COLORS.text,
    border: COLORS.secondary,
    fontFamily: "Poppins",}}>
                        {item.meeting_id}
                      </TableCell>
                      <TableCell sx={{color: COLORS.text,
    border: COLORS.secondary,
    fontFamily: "Poppins",}}>
                        {item.recording_type}
                      </TableCell>
                      <TableCell sx={{color: COLORS.text,
    border: COLORS.secondary,
    fontFamily: "Poppins",}}>
                        {(item.file_size / (1024 * 1024)).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{color: COLORS.text,
    border: COLORS.secondary,
    fontFamily: "Poppins",}}>
                        {formatDate(item.recording_start)}
                      </TableCell>
                      <TableCell sx={{color: COLORS.text,
    border: COLORS.secondary,
    fontFamily: "Poppins",}}>
                        {formatDate(item.recording_end)}
                      </TableCell>

                      <TableCell sx={{color: COLORS.text,
    border: COLORS.secondary,
    fontFamily: "Poppins",}}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          sx={{
                            background: COLORS.button_edit_bg,
                            "&:hover": {
                              background: COLORS.hover_edit_btn,
                            },
                            color: COLORS.background,
                            fontFamily: "Poppins",
                            textTransform: "none",
                            mr: 1,
                            padding: "4px 8px",}}
                          onClick={() => handleViewVideo(item.meeting_id)}
                        >
                          View
                        </Button>

                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<DownloadIcon />}
                          sx={{
                              background: COLORS.button_red_bg,
                              color: COLORS.background,
                              "&:hover": { background: COLORS.hover_red_btn },
                              fontFamily: "Poppins",
                              textTransform: "none",
                              padding: "4px 8px",}}
                              // onClick={() => window.open(item.s3_url, "_blank")}
                              onClick={async () => {
                                      try {
                                        const response = await api.get(
                                          `/api/recordings_signed_url/${item.meeting_id}`
                                        );
                                    
                                        const url = response.data?.recordings?.[0]?.presigned_url;
                                    
                                        if (url) {
                                          window.open(url, "_blank");
                                        } else {
                                          Toast.error("No recording found");
                                        }
                                      } catch (error) {
                                        Toast.error("Download failed");
                                      }
                                    }}
                        >
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[5, 10, 15]}
              component="div"
              count={filteredVideos.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                background: COLORS.primary,
                color: COLORS.text,
                fontFamily: "Poppins",
              }}
            />
          </TableContainer>
        </Grid>
      </Grid>

      {/* Video Player Modal */}
      <Modal open={openPlayer} onClose={() => setOpenPlayer(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "70%",
            bgcolor: "black",
            boxShadow: 24,
            p: 2,
            borderRadius: "8px",
          }}
        >
          <video
            src={currentVideoUrl}
            controls
            autoPlay
            style={{ width: "100%", borderRadius: "8px" }}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default ZoomMeeting;

// const styles = {
//   headerCell: {
//     color: COLORS.text,
//     borderBottom: COLORS.secondary,
//     fontWeight: "bold",
//     fontFamily: "Poppins",
//   },
//   bodyCell: {
//     color: COLORS.text,
//     border: COLORS.secondary,
//     fontFamily: "Poppins",
//   },
//   viewBtn: {
//     background: COLORS.button_edit_bg,
//     "&:hover": {
//       background: COLORS.hover_edit_btn,
//     },
//     color: COLORS.background,
//     fontFamily: "Poppins",
//     textTransform: "none",
//     mr: 1,
//     padding: "4px 8px",
//   },
//   downloadBtn: {
//     background: COLORS.button_red_bg,
//     color: COLORS.background,
//     "&:hover": { background: COLORS.hover_red_btn },
//     fontFamily: "Poppins",
//     textTransform: "none",
//     padding: "4px 8px",
//   },
// };
